import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NgForm } from '@angular/forms';

import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import {
  catchError,
  combineLatest,
  debounceTime,
  finalize,
  of,
  Subject,
  Subscription,
  take,
  tap,
} from 'rxjs';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import { Moment } from 'moment-timezone';

import { Schedule } from '@shared/schedule/models';
import { MergedCustomFields, Party } from '@shared/party/models';
import { BlockedTimeResult } from '@shared/blocked-time/models';
import { ICapacity, ResourceAssignedToFloorplan, Resource } from '@shared/resource/models';
import { BookingOptions, ResourceSettings, Restaurant } from '@shared/restaurant/models';
import { PartySource, PartyTypes } from '@shared/party/enums';
import { isUsOrCaPhoneNumber, useWhatsApp } from '@shared/sms/functions';
import { extractPartyFieldsFromCustomer } from '@shared/customer/functions';
import { mergeCustomFields, canBypassNewPartyValidation, partyCriticalFieldsUnchanged, PartyCriticalFields, partyIdsEqual, isPartySeated } from '@shared/party';
import { describeOutOfBusinessHours } from '@shared/schedule/functions';
import { ResourceSuggestionOption } from '@shared/resource/enums';
import {
  CommonRadioButtonItem,
  ConfigurePartyService,
  ConfirmationModalService,
  CustomDateParserFormatterProvider,
  CustomTimepickerModelAdapterProvider,
  DateTimeService,
  FloorplanResources,
  IParty,
  IServiceWaitlist,
  ProviderScheduleService,
  TelService,
  calculateShortenedDurationState,
  getServiceOptionNameWithWarning,
  ShortenedDurationState,
} from '@tablesready/common-ui';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon } from '@tablesready/frontend-icons';
import { GuiderService } from '@tablesready/frontend-guider';
import { AlertsService, ClockService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { CustomerInfo } from '@tablesready/frontend-party-customer-info';
import {
  AutoDurationRecalculator,
  DateService,
  IPartyOnWaitlist,
  MarkPartyService,
  ResourcesService,
  ServicesService,
  seatedConflictConfirm,
  saveAnywayConfirm,
  saveOrSeatAction,
  buildSaveAnywayApproval,
  SaveAnywayApproval,
  tableTakenWarning,
  keepGrantedSeatOverride,
} from '../../services';
import { WaitTimeService } from '@tablesready/frontend-party-observer';
import { PartiesService } from '@tablesready/frontend-api-services';
import { CustomersService } from '../../customers/services';
import { AddEditPartyService } from '../../services/add-edit-party.service';

@Component({
  selector: 'tablesready-add-edit-party-floorplan',
  templateUrl: './waitlist-add-edit-party-floorplan.component.html',
  styleUrls: ['./waitlist-add-edit-party-floorplan.component.scss'],
  standalone: false,
  providers: [
    CustomDateParserFormatterProvider,
    CustomTimepickerModelAdapterProvider,
  ],
})
export class WaitlistAddEditPartyFloorplanComponent implements OnInit, OnDestroy {
  @ViewChild('addEditForm') addEditForm: NgForm;
  @Input() partyToEdit: IParty;
  @Input() typeOfParty: PartyTypes;
  @Input() userSchedule;
  @Input() user: Restaurant;
  @Input() parties: IParty[];
  @Input() showSelectResourceMessage: boolean;
  @Input() resourceOverrideCallback?: (party: IParty) => void;
  revealForm?: () => void;
  abortHandOff?: () => void;
  @Input() selectedResource: Resource;
  @Input() selectedTime: number;
  @Input() floorplanResources: FloorplanResources[];
  @Input() afterInit?: (instance: WaitlistAddEditPartyFloorplanComponent) => void;
  @Input() confirmBtnText = 'Confirm';
  @Input() skipConfirmation = false;
  @Input() skipName = false;
  @Input() availableOnBusinessPlan = false;
  icon = AppIcon;
  title: string;
  selectedTimeMode = false;
  isSubmitting = false;
  isSavingParty = false;
  now;
  resourcesInDropdown: ResourceAssignedToFloorplan[] = [];
  servicesInDropdown: IServiceWaitlist[] = [];
  msPerMin = 60000;
  shortestPhoneDigits = 4;
  originalQuotedTime = 0;
  timezone: string;
  currentTime: Moment;
  isWorkTime = false;
  resourceSizeAlert: string | null = null;
  generalSchedule: Schedule[];
  overrideNoResources = false;
  overrideBlockedTime = false;
  blockedTime?: BlockedTimeResult;
  exceptions;
  reservationMinutesPast = 420;
  sizes = [1, 2, 3, 4, 5, 6, 7];
  sizeOther: number;
  party: IPartyOnWaitlist;
  time;
  date;
  workHours: Array<[number, number]>;
  showOverlappingAlert;
  showResourceAlert = false;
  availablePartyDuration;
  availablePartyDurationInMin;
  errorServices = false;
  removeServices = false;
  removeResources = false;
  showSizeValidationMessage;
  schedule;
  overrideIsWorkTime = false;
  // Date
  minDate: NgbDateStruct;
  maxDate: NgbDateStruct;
  allowMultipleParties: boolean;
  overlappingParty: Party[];
  userResourceSettings: ResourceSettings;
  bookingOptions: BookingOptions;
  angularticsActionType: string;
  sizesRadioItems: CommonRadioButtonItem[] = [];
  partySources = PartySource;
  resourceSuggestionOption = ResourceSuggestionOption;
  customer: CustomerInfo = null;
  displayOptionsReservationsLabelPlural: string;
  resourcesEnabled = false;
  servicesEnabled = false;
  // Shortened duration tracking
  shortenedDurationState: ShortenedDurationState | null = null;
  shortenedDurationConfirmed = false;

  private isEditMode = false;
  private originalCritical: PartyCriticalFields | null = null;
  private hasDisabledServicesOnInit = false;
  private readonly durationRecalc = new AutoDurationRecalculator({
    getServices: () => this.servicesInDropdown,
    getSelectedServiceIds: () => this.party.services || [],
    getPartySize: () => this.party.size,
    getDuration: () => this.party.duration,
    applyDuration: (duration) => {
      this.party.duration = duration;
      this.party.durationInMinutes = Math.round(duration / this.msPerMin) || 0;
    },
  });

  @ViewChild('confirmModalContent') confirmModalContentRef: ElementRef;
  private readonly getAvailableResourcesServices$ = new Subject<{ date: string, party: IParty }>();
  private readonly subscription = new Subscription();

  constructor(
    private readonly modalController: FrontendModalService,
    private readonly alertsService: AlertsService,
    private readonly waitTimeService: WaitTimeService,
    private readonly dateService: DateService,
    private readonly partiesService: PartiesService,
    private readonly configureParty: ConfigurePartyService,
    private readonly clockService: ClockService,
    private readonly telService: TelService,
    private readonly markPartyService: MarkPartyService,
    readonly guiderService: GuiderService,
    private readonly dateTimeService: DateTimeService,
    private readonly providerScheduleService: ProviderScheduleService,
    private readonly customerService: CustomersService,
    private readonly addEditPartyService: AddEditPartyService,
    private readonly servicesService: ServicesService,
    private readonly resourcesService: ResourcesService,
    private readonly confirmationModalService: ConfirmationModalService,
  ) { }

  isOutOfBusinessHours({ form: addEditForm }: NgForm) {
    // Editing an existing party must never re-run new-party business-hours validation —
    // it was already valid when saved; only a brand-new party needs this check.
    if (this.isEditMode && isPartySeated(this.partyToEdit)) {
      return false;
    }

    return !this.isWorkTime &&
      !addEditForm.pristine &&
      !this.overrideIsWorkTime &&
      (this.typeOfParty !== PartyTypes.reservation ||
        addEditForm.controls.time?.value?.includes('M'));
  }

  getOutOfBusinessHoursMessage(): string {
    return describeOutOfBusinessHours(
      this.currentTime.valueOf(),
      this.party?.duration || 0,
      this.workHours || [],
      this.timezone,
    );
  }

  canBypassWorkTime(): boolean {
    return canBypassNewPartyValidation(
      this.isEditMode, this.partyToEdit, this.originalCritical, () => this.snapshotCritical(),
    );
  }

  private captureOriginalCritical(): void {
    this.originalCritical = this.snapshotCritical();
  }

  // quotedTime uses currentTime (the live, edited reservation time). this.party.quoted_time is only
  // written at save time (convertQuotedTime), so it is stale during editing.
  private snapshotCritical(): PartyCriticalFields {
    return {
      size: this.party.size,
      duration: this.party.duration,
      quotedTime: this.currentTime.valueOf(),
      services: [...(this.party.services ?? [])],
      resources: [...(this.party.resources ?? [])],
      seatedTime: this.partyToEdit.seated_time ?? null,
      checkinTime: this.partyToEdit.checkin_time ?? null,
    };
  }

  private criticalFieldsUnchanged(): boolean {
    return partyCriticalFieldsUnchanged(this.originalCritical, () => this.snapshotCritical());
  }

  get isAvailableTime() {
    return this.showResourceAlert &&
      !this.resourcesInDropdown.length &&
      this.resourcesEnabled;
  }

  get isWalkin() {
    return this.typeOfParty === PartyTypes.walkIn;
  }

  get isReservation() {
    return this.typeOfParty === PartyTypes.reservation;
  }

  get isNoServices(): boolean {
    return !this.servicesEnabled || !this.party?.services?.length;
  }

  /**
   * Whether the schedule warning with its "Override?" checkbox is on screen. Bound by the template
   * so the warning and the Confirm button can never disagree: Confirm is disabled while this shows
   * and the override is un-ticked, and — just as important — is not blocked by business hours when
   * no warning is up, so an existing party stays editable on a closed day.
   */
  showBusinessHoursWarning(addEditForm: NgForm): boolean {
    return this.isOutOfBusinessHours(addEditForm) &&
      (this.isNoServices || Boolean(this.party?.services?.length));
  }

  /**
   * Same contract for the "no services available" warning, which offers no override. A seated party
   * is never re-validated against current availability (it was necessarily seated in the past), so
   * an empty service list says nothing about it and must not block the edit.
   */
  showNoServicesWarning(addEditForm: NgForm): boolean {
    return !this.isOutOfBusinessHours(addEditForm) && this.errorServices && !isPartySeated(this.partyToEdit);
  }

  ngOnInit(): void {
    const getAvailableResourcesSub = this.getAvailableResourcesServices$
      .pipe(debounceTime(500))
      .subscribe((data) => {
        this.getAvailableResourcesServicesInternal(data.date, data.party);
      });
    this.subscription.add(getAvailableResourcesSub);

    this.subscription.add(this.clockService.clock$.subscribe(now => this.now = now));
    this.sizesRadioItems = [1, 2, 3, 4, 5, 6, 7].map((size) => ({
      name: String(size),
      value: size,
      onClick: () => this.sizeChanged(),
    }));

    this.initModalProp();
    this.displayOptionsReservationsLabelPlural = this.user?.displayOptions?.labels?.reservation_plural || 'reservations';
    this.minDate = this.dateTimeService.momentToNgbDate(this.currentTime);
    this.maxDate = {
      ...this.minDate,
      year: this.minDate.year + 1,
    };
    if (this.partyToEdit) {
      this.initEditMode();
      this.checkWorkTime();
    } else if (this.selectedTime && this.selectedResource) {
      this.initAddModeWithSelectedResourceAndTime(this.selectedTime);
    } else {
      this.initAddMode();
    }
    this.setTitle();

    if (this.afterInit) {
      this.afterInit(this);
    }
  }

  onNext() {
    this.modalController.dismiss();
  }

  onPrev() {
    this.modalController.dismiss();
  }

  doesEditingHaveAnyConflicts(): Record<string, boolean> {
    return {
      editFormInvalid: this.addEditForm.form.invalid,
      isOutOutBusinessHours: !this.isOutOfBusinessHours(this.addEditForm) && this.errorServices,
      isAvailableTimeAndNoOverride: this.isAvailableTime && !this.overrideNoResources,
      noConfirm: this.isConfirmPartyButtonDisabled(),
      formPending: this.addEditForm.form.pending,
      submitting: this.isSubmitting,
    };
  }

  addResourceToSelectedOnes(resourceId: string, serviceIds: string[]): void {
    const doesExist = this.party?.resources?.findIndex((id) => id === resourceId);
    if (doesExist !== -1) {
      return;
    }

    this.party.resources = [...new Set([...this.party.resources, resourceId])];
    this.party.services = [...serviceIds];
    this.selectResources();
  }

  initModalProp() {
    this.generalSchedule = (this.userSchedule || []).filter(schedule => schedule.default);

    try {
      this.schedule = this.generalSchedule[0].hours;
    } catch (e) {
      this.alertsService.add({
        type: IAlertTypes.danger,
        heading: 'Please fill in work schedule in Settings -> Schedules',
        body: '',
      });
    }

    this.timezone = this.user.timezone;
    this.userResourceSettings = this.user?.resourceSettings;
    this.bookingOptions = this.user?.bookingOptions;
    this.allowMultipleParties = this.user?.bookingOptions?.allowMultipleParties;
    this.currentTime = moment.tz(this.timezone);
    this.exceptions = this.generalSchedule[0]?.exceptions || [];
    this.showSelectResourceMessage = this.showSelectResourceMessage || false;

    const subscription = combineLatest([
      this.resourcesService.isEnabled(),
      this.servicesService.isEnabled(),
    ])
      .pipe(tap(([resourcesEnabled, servicesEnabled]) => {
        this.resourcesEnabled = resourcesEnabled;
        this.servicesEnabled = servicesEnabled;
        this.overrideNoResources = !(this.servicesEnabled || this.user.bookingOptions?.onlineReservationsEnabled);
      }))
      .subscribe();
    this.subscription.add(subscription);
  }

  setTitle(): void {
    let text = this.user?.displayOptions?.labels?.customer || 'customer';

    if (this.typeOfParty === PartyTypes.reservation) {
      text = this.user.displayOptions?.labels?.reservation || 'reservation';
    }

    this.title += text;
  }

  initEditMode(): void {
    this.title = 'Edit ';
    this.angularticsActionType = 'edited waiting party';
    this.party = cloneDeep(this.partyToEdit) as IPartyOnWaitlist;
    this.selectedTimeMode = false;
    this.isEditMode = true;
    const quotedTime = moment.tz(this.party.quoted_time, this.timezone);
    this.party.durationInMinutes = Math.round(this.party.duration / this.msPerMin || 0);
    this.party.phone = this.telService.format(this.party.phone, this.user.country, 'custom');
    this.party.quoted_time_minutes = this.typeOfParty === PartyTypes.walkIn ? Math.round((this.party.quoted_time - this.now) / this.msPerMin) : 0;
    this.party.date = this.dateTimeService.momentToNgbDate(quotedTime);
    this.party.time = quotedTime.format('h:mm A');
    // Only enabled services/resources are shown as selected and offered in the dropdown — a
    // disabled assignment is never user-visible or user-selectable. The true original ids
    // (this.partyToEdit.services/.resources) remain available for the autoselect guard below and
    // for saveParty(), which restores them on save if the user never touched these fields, so a
    // disabled assignment is preserved rather than silently dropped by an unrelated edit.
    const enabledServices = this.servicesService.getEnabledByIds(this.partyToEdit.services);
    this.hasDisabledServicesOnInit = enabledServices.length !== (this.partyToEdit.services || []).length;
    this.party.services = enabledServices.map(service => service._id);
    if (this.hasDisabledServicesOnInit) {
      this.party.duration = this.dateService.checkServicesDuration(enabledServices, this.party.services, this.party.size);
      this.party.durationInMinutes = this.party.duration / this.msPerMin || 0;
    }
    this.durationRecalc.setBaseline(
      this.dateService.checkServicesDuration(enabledServices, this.party.services, this.party.size),
      this.party.services,
    );
    this.party.resources = this.resourcesService.getEnabledByIds(this.partyToEdit.resources).map(resource => resource._id);
    this.originalQuotedTime = this.party.quoted_time;
    this.currentTime = moment(this.party.quoted_time).tz(this.timezone);
    this.setPartySize(this.party.size);

    this.isWorkTime = true;
    this.captureOriginalCritical();
  }

  setPartySize(size) {
    if (size < 8) {
      this.party.size = size;

      return true;
    }
    this.sizeOther = this.party.size;
  }

  initAddModeWithSelectedResourceAndTime(time: number): void {
    this.title = 'Add ';
    this.angularticsActionType = 'added party to waitlist';
    this.selectedTimeMode = true;
    const selectedTime = this.dateTimeService.roundingMinutes(Number(time), 15, this.timezone);
    this.resourcesInDropdown = [this.selectedResource];
    this.party = {
      checkin_time: 0,
      created_time: 0,
      durationInMinutes: 0,
      name: '',
      phone: '',
      quoted_time_minutes: undefined,
      sizeOther: undefined,
      quoted_time: this.typeOfParty === PartyTypes.reservation ? 0 : this.waitTimeService.getTime(),
      size: this.selectedResource.capacity.max,
      type: this.getPartyType(this.typeOfParty),
      date: this.dateTimeService.momentToNgbDate(selectedTime),
      time: selectedTime.format('h:mm A'),
      resources: [this.selectedResource._id],
      services: [],
      messagingProvider: null,
      customFields: {},
    };
    this.currentTime = selectedTime;
    this.checkWorkTime();
  }

  initAddMode() {
    this.title = 'Add ';
    this.angularticsActionType = 'added party to waitlist';
    this.selectedTimeMode = false;
    this.party = {
      checkin_time: 0,
      created_time: 0,
      durationInMinutes: 0,
      name: '',
      phone: '',
      quoted_time: 0,
      sizeOther: undefined,
      quoted_time_minutes: this.typeOfParty === PartyTypes.reservation ? 0 : this.waitTimeService.getTime(),
      size: this.user.default_size,
      type: this.getPartyType(this.typeOfParty),
      date: this.dateTimeService.momentToNgbDate(this.currentTime),
      messagingProvider: null,
      customFields: {},
    };
    (this.party as any).quoted_time = this.currentTime.add(this.party.quoted_time_minutes, 'minutes');
    this.checkWorkTime();
    this.setPartySize(this.party.size);
  }

  getPartyType(typeOfParty: PartyTypes) {
    return typeOfParty === PartyTypes.reservation ? PartyTypes.reservation : PartyTypes.walkIn;
  }

  checkWorkTime() {
    if (!this.servicesEnabled &&
      !this.user?.bookingOptions?.onlineReservationsEnabled &&
      this.resourcesEnabled) {
      this.getAvailableResourcesServices$.next({
        date: this.currentTime.format('x'),
        party: this.party,
      });
    }
    if (this.typeOfParty === PartyTypes.reservation && !this.party.time) {
      return false;
    }
    this.isSubmitting = true;
    this.time = this.party.time || this.currentTime.format('h:mm A');
    this.date = this.currentTime.format('D-M-YYYY');
    const { hours, exceptions } = this.generalSchedule[0];
    this.workHours = this.providerScheduleService
      .getWorkingHours(hours, exceptions, this.currentTime.valueOf(), this.timezone);

    if (!this.criticalFieldsUnchanged() && this.party.duration <= 0) {
      this.party.duration = this.dateService.checkServicesDuration(this.servicesInDropdown, this.party.services, this.party.size);
    }

    this.party.durationInMinutes = Math.round(this.party.duration / this.msPerMin);

    this.isWorkTime = this.providerScheduleService
      .comparisonTime(this.currentTime.valueOf(), this.workHours, this.party.duration);
    if (this.servicesEnabled || this.resourcesEnabled) {
      this.getAvailableResourcesServices$.next({
        date: this.currentTime.format('x'),
        party: this.party,
      });
    } else {
      this.isSubmitting = false;
    }
  }

  getAvailableResourcesServicesInternal(date, party: IParty) {
    const { services, resources, size, duration } = party;
    if (!date && !services && !resources) {
      return false;
    }

    const type = party.type === PartyTypes.reservation ? party.type : PartyTypes.walkIn;
    const dateMilliseconds = this.dateTimeService.ngbDateToMoment(this.party.date, this.timezone).valueOf();
    const { hours, exceptions } = this.generalSchedule[0];
    const hoursWithDate = this.providerScheduleService.getWorkingHours(
      hours,
      exceptions,
      dateMilliseconds,
      this.timezone,
    );

    // The availability query does an exact-match intersection against each resource's services on
    // the backend, so a disabled id (kept in `services` to protect it from autoselect) must not be
    // sent here — it would zero out every resource that doesn't also carry that disabled service.
    const enabledServiceIds = this.servicesService.getEnabledByIds(services).map(service => service._id);

    this.partiesService.findResourcesServicesForNewParty(
      date,
      type,
      this.overrideNoResources ? enabledServiceIds : [],
      resources,
      size,
      duration,
      party._id,
      this.overrideNoResources,
      hoursWithDate,
    )
      .subscribe(
        (data) => {
          let resourcesCapacity: ICapacity = null;
          this.showOverlappingAlert = false;
          this.overlappingParty = data?.overlappingParty;
          this.blockedTime = data?.blockedTime;
          if (data?.availableResources) {
            this.resourcesInDropdown = this.addEditPartyService.placeResourcesFitBySizeFirst<ResourceAssignedToFloorplan>(data.availableResources, size)
              .filter((resource) => {
                let allPartyServicesPresentInResource = true;

                if (party?.services?.length > 0) {
                  for (const item of party?.services) {
                    if (!allPartyServicesPresentInResource) {
                      return;
                    }

                    if (!resource.services.includes(item)) {
                      allPartyServicesPresentInResource = false;
                      break;
                    }
                  }
                }

                return /*size >= resource.capacity.min &&*/ allPartyServicesPresentInResource;
              })
              .map((resource: ResourceAssignedToFloorplan) => {
                let isResourceOnFloorplan = false;
                if (this.floorplanResources) {
                  for (const item of this.floorplanResources) {
                    if (item.resources.includes(resource._id) && !isResourceOnFloorplan) {
                      isResourceOnFloorplan = true;

                      resource.onFloorplan = this.floorplanResources.length > 1
                        ? `${item.floorPlanName}`
                        : '';
                    }
                  }

                  if (!isResourceOnFloorplan) {
                    resource.onFloorplan = 'not in floorplan';
                  }
                }

                return resource;
              });

            // If a resource is missing from this list (e.g. it was disabled) clear it — but only
            // when something critical actually changed. An edit that never touches
            // services/resources/size/duration/time must leave the existing (possibly
            // since-disabled) assignment alone.
            if (this.resourcesInDropdown.length && party.resources && !this.canBypassWorkTime()) {
              party.resources = party.resources.filter(resourceId => {
                const resourceExists = this.resourcesInDropdown.find(resource => resource._id === resourceId);

                return Boolean(resourceExists);
              });
            }

            if (this.resourcesEnabled) {
              this.showResourceAlert = true;
            }

            resourcesCapacity = this.calculateCapacityInSelectedResources(this.resourcesInDropdown, resources);
          }
          if (data?.availableServices) {
            this.servicesInDropdown = data.availableServices;

            if (!this.criticalFieldsUnchanged() && this.party.services?.length && this.party.duration <= 0) {
              this.party.duration = this.dateService.checkServicesDuration(this.servicesInDropdown, this.party.services, this.party.size);
              this.party.durationInMinutes = Math.round(this.party.duration / this.msPerMin);
            }

            if (this.party.services && this.party.services.length > 0) {
              const prevRequiresConfirmation = this.shortenedDurationState?.requiresConfirmation ?? false;
              this.shortenedDurationState = calculateShortenedDurationState(this.servicesInDropdown, this.party?.services || []);
              // In edit mode never write the adjusted duration back (it would silently change the party's duration).
              if (
                !this.canBypassWorkTime() &&
                this.shortenedDurationState.adjustedDurationMs !== null &&
                this.party.durationInMinutes > this.shortenedDurationState.adjustedDurationMs / this.msPerMin
              ) {
                this.party.duration = this.shortenedDurationState.adjustedDurationMs;
                this.party.durationInMinutes = Math.round(this.shortenedDurationState.adjustedDurationMs / this.msPerMin);
              }
              if (!this.shortenedDurationState.requiresConfirmation) {
                this.shortenedDurationConfirmed = true;
              } else if (!prevRequiresConfirmation) {
                this.shortenedDurationConfirmed = false;
              }
            }
          }

          if (!this.overlappingParty) {
            this.overlappingParty = [];
          }

          if (this.overlappingParty.length && !(this.bookingOptions?.allowMultipleParties || this.overrideNoResources)) {
            this.resourcesInDropdown = [];
            // Skip in edit mode: do not destroy an existing party's resources on overlap.
            if (!this.canBypassWorkTime()) {
              this.party.resources = [];
            }
            this.showOverlappingAlert = true;
            const overlappingParty = data.overlappingParty[0];
            this.availablePartyDuration = overlappingParty.quoted_time - date;
            this.availablePartyDurationInMin = Math.round(this.availablePartyDuration / this.msPerMin);
          } else {
            this.showSizeAlert(resourcesCapacity);
          }

          // Protect an existing party's already-assigned service/resource from being silently swapped
          // or filled in by this autoselect. These check the party's true original assignment
          // (this.partyToEdit), not the enabled-only `services`/`resources` shown in the dropdown —
          // a disabled assignment is hidden from the picker, but it still counts as "already assigned"
          // for this guard, and only counts as absent when the party truly never had one.
          const blocksServiceAutoselect = this.canBypassWorkTime() && (this.partyToEdit?.services?.length ?? 0) > 0;
          const blocksResourceAutoselect = this.canBypassWorkTime() && (this.partyToEdit?.resources?.length ?? 0) > 0;
          if (!this.servicesInDropdown.length && this.servicesEnabled) {
            this.errorServices = true;
          } else if (this.servicesInDropdown?.length === 1 && !this.removeServices) {
            if (!blocksServiceAutoselect && (!services?.length || this.servicesInDropdown[0]._id !== services[0])) {
              // Autoselect service
              this.party.services = [this.servicesInDropdown[0]._id];
              this.selectServices();
            } else if (services?.length === 1 && !resources?.length && !blocksResourceAutoselect) {
              // Autoselect resource
              this.resourcesInDropdown = this.resourcesInDropdown.filter(resource => resource.services.includes(services[0]));
              if (
                this.resourcesEnabled &&
                this.user.resourceSettings?.resourceSuggestion === this.resourceSuggestionOption.auto &&
                this.resourcesInDropdown.length > 0 &&
                !this.removeResources &&
                !this.selectedResource
              ) {
                let resourceIdToSelect: string;

                for (const availableResource of data?.availableResources) {
                  if (
                    availableResource.services.includes(services[0]) &&
                    size >= availableResource.capacity.min &&
                    size <= availableResource.capacity.max
                  ) {
                    // We don't want to pre-select a resource that doesn't fit party size
                    // And we've already filtered resources by min capacity
                    resourceIdToSelect = availableResource._id;

                    break;
                  }
                }
                if (resourceIdToSelect !== undefined) {
                  this.party.resources = [resourceIdToSelect];
                  this.selectResources();
                }
              }
            }
          } else {
            this.errorServices = false;
          }
          this.isSubmitting = false;

          if (this.resourceOverrideCallback) {
            this.resourceOverrideCallback(this.party);
          }
        },
        (error) => {
          if (error.status === 401) {
            this.cancel();
          } else {
            this.alertsService.add({
              type: IAlertTypes.danger,
              heading: 'Error:',
              body: `resources and services not found: ${error}`,
            });
          }
        },
      );
  }

  calculateCapacityInSelectedResources(resources: Resource[] = [], selectedResourceIds: string[] = []): ICapacity | null {
    const fullDataOfSelectedResources = resources.filter(resource => selectedResourceIds.includes(resource._id));

    return this.calculateResourcesCapacity(fullDataOfSelectedResources);
  }

  calculateResourcesCapacity(resources = []) {
    return resources.reduce((acc, resource) => {
      acc.min += resource.capacity.min || 0;
      acc.max += resource.capacity.max || 0;

      return acc;
    }, {
      min: 0,
      max: 0,
    });
  }

  selectServices() {
    if (this.party.services && this.party.services.length === 0) {
      // Reset errors if services removed from party
      this.removeServices = true;
      this.errorServices = false;
      this.showResourceAlert = false;
      this.shortenedDurationState = null;
      this.durationRecalc.recompute();
      this.checkWorkTime();

      return;
    }
    this.removeServices = false;
    if (this.resourcesEnabled) {
      this.showResourceAlert = true;
    }
    this.showResourceAlert = true;
    this.party.duration = 0;
    this.durationRecalc.recompute();

    // Calculate shortened duration state and apply adjusted duration if needed
    const prevRequiresConfirmation = this.shortenedDurationState?.requiresConfirmation ?? false;
    this.shortenedDurationState = calculateShortenedDurationState(this.servicesInDropdown, this.party?.services || []);
    if (this.shortenedDurationState.adjustedDurationMs !== null) {
      this.party.duration = this.shortenedDurationState.adjustedDurationMs;
      this.party.durationInMinutes = Math.round(this.shortenedDurationState.adjustedDurationMs / this.msPerMin);
    }

    if (!this.shortenedDurationState.requiresConfirmation) {
      this.shortenedDurationConfirmed = true;
    } else if (!prevRequiresConfirmation) {
      this.shortenedDurationConfirmed = false;
    }

    this.checkWorkTime();
  }

  selectResources(): void {
    this.durationRecalc.recompute();
    this.showResourceAlert = true;

    if (this.party.resources?.length) {
      this.removeResources = false;
      const resourcesCapacity = this.calculateCapacityInSelectedResources(this.resourcesInDropdown, this.party.resources);
      this.showSizeAlert(resourcesCapacity);
    } else {
      this.removeResources = true;
      this.showSizeAlert(null);
    }

    this.checkWorkTime();
  }

  overrideResources() {
    this.checkWorkTime();
  }

  clearServicesResources() {
    this.party.services = [];
    this.party.resources = [];
  }

  setQuotedTime() {
    this.currentTime = moment.tz(this.timezone);
    this.currentTime.add(this.party.quoted_time_minutes, 'minutes');
    this.clearServicesResources();
    this.checkWorkTime();
  }

  changeQuoted(num, model = 'quoted_time_minutes', modelToAddMilliseconds = '') {
    this.party[model] += num;
    if (modelToAddMilliseconds) {
      this.party[modelToAddMilliseconds] += num * this.msPerMin;
    } else {
      this.clearServicesResources();
    }
    if (model === 'quoted_time_minutes') {
      this.currentTime.add(num, 'minutes');
    }
    this.checkWorkTime();
  }

  getItemOptionName = (item: ResourceAssignedToFloorplan): string => {
    return this.getItemName(item, true);
  };

  getItemName(item: ResourceAssignedToFloorplan, addCapacity = false): string {
    let result = item?.name || '';

    if (addCapacity && item?.capacity) {
      result += ` (${item.capacity.max}) `;
    }

    if (item?.onFloorplan) {
      result += ` [${item.onFloorplan}]`;
    }

    return result;
  }

  convertQuotedTime(party) {
    const resTime = moment.tz(this.timezone);
    if (this.party.type === PartyTypes.reservation && this.currentTime.format('x') < resTime.add(-this.reservationMinutesPast, 'minutes').format('x')) {
      this.isWorkTime = false;

      return;
    }
    delete party.date;
    delete party.time;
    delete party.durationInMinutes;
    delete party.quoted_time_minutes;
    delete party.className;
    delete party.assignedResources;

    const updateWaitTime = !party._id && party.type === PartyTypes.walkIn; // Update wait time if new party
    party.quoted_time = party.quoted_time || 0;
    if (updateWaitTime && !(
      this.originalQuotedTime > 0 &&
      party.quoted_time > this.originalQuotedTime - this.msPerMin &&
      party.quoted_time < this.originalQuotedTime + this.msPerMin
    )) {
      this.waitTimeService.setTime(Math.round((party.quoted_time - this.now) / this.msPerMin));
    }
    party.quoted_time = Number(this.currentTime.format('x'));
  }

  showSizeAlert(resourcesCapacity: ICapacity | null): void {
    const message = this.resourcesInDropdown?.length
      ? this.configureParty.buildResourceSizeAlert({
          actualSize: this.party?.size,
          capacity: resourcesCapacity,
          customerLabel: this.user.displayOptions?.labels?.customer || 'customer',
          resourceLabel: this.user.resourceSettings?.label || 'resource',
          allowMultipleParties: this.allowMultipleParties,
          partySizeForTimeSlots: this.bookingOptions?.partySizeForTimeSlots,
          overlappingParties: this.overlappingParty,
        })
      : null;
    this.resourceSizeAlert = message;
    if (message) {
      this.removeResources = true;
    }
  }

  sizeChanged() {
    this.showSizeValidationMessage = false;
    const { resources, size } = this.party;
    if (this.resourcesEnabled && this.party?.resources?.length) {
      const resourcesCapacity = this.calculateCapacityInSelectedResources(this.resourcesInDropdown, resources);
      if (size < resourcesCapacity.min || size > resourcesCapacity.max) {
        // RemoveResources
        this.party.resources = [];
      }
      this.showSizeAlert(resourcesCapacity);
      this.sizeOther = size < 8 ? undefined : size;

      return false;
    }
    if (this.resourcesEnabled && !this.party?.resources?.length) {
      this.resourceSizeAlert = null;
    }
    this.checkWorkTime();
  }

  changeSize() {
    this.showSizeValidationMessage = false;
    this.party.size = this.sizeOther || this.user.default_size;
    this.sizeChanged();
  }

  partyPhoneLookup(): void {
    const { phone } = this.party;
    if (!phone ||
      phone === '' ||
      typeof phone === 'undefined' ||
      phone.length < this.shortestPhoneDigits
    ) {
      return;
    }
    const parsedPhone = this.telService.format(phone, this.user.country, 'intl');
    if (typeof parsedPhone !== 'undefined' && parsedPhone.trim().length >= this.shortestPhoneDigits) {
      this.customerService.getByPhone(parsedPhone)
        .pipe(
          tap(data => {
            if (data) {
              const customerCustomFields: MergedCustomFields = extractPartyFieldsFromCustomer(this.user.partyFields, data);
              const _data = {
                name: this.party.name || customerCustomFields.name,
                customFields: mergeCustomFields(this.party.customFields, customerCustomFields.customFields),
              };
              this.party = {
                ...this.party,
                ..._data,
              };
              this.customer = data;
            } else {
              this.customer = null;
            }
          }),
        )
        .subscribe();
    }
  }

  onResourceInputClear(): void {
    this.sizeChanged();
  }

  clearSelectedResource(item: Resource, doClear?: (res: Resource) => void): void {
    if (doClear) {
      doClear(item);
    }

    this.sizeChanged();
  }

  changeDuration() {
    this.party.duration = this.party.durationInMinutes * this.msPerMin;
    this.checkWorkTime();
  }

  isConfirmPartyButtonDisabled() {
    return (this.showOverlappingAlert || !!this.resourceSizeAlert);
  }

  changeDate() {
    if (
      this.selectedTimeMode ||
      !this.party.time ||
      !this.party.time.includes('M') ||
      typeof this.party.date === 'string' ||
      this.party.date === null
    ) {
      return false;
    }

    this.currentTime = this.dateTimeService.setTimeToDate(
      this.dateTimeService.ngbDateToMoment(this.party.date, this.timezone),
      this.party.time,
      this.timezone,
    );

    if (!(!this.servicesEnabled &&
      !this.user?.bookingOptions?.onlineReservationsEnabled &&
      this.resourcesEnabled)) {
      this.clearServicesResources();
    }
    this.checkWorkTime();
  }

  clean(obj) {
    for (const propName in obj) {
      if (propName === 'messagingProvider') {
        continue;
      }
      if (obj[propName] === null || obj[propName] === undefined) {
        delete obj[propName];
      }
    }
  }

  saveParty(showSuccessUpdateAlert = true): void {
    const party = {
      ...this.party,
      skipConfirmation: this.skipConfirmation,
    };
    this.clean(party);
    if (party.phone) {
      party.phone = this.telService.format(party.phone, this.user.country, 'intl');
    }

    this.convertQuotedTime(party);
    if (this.isWorkTimeBlockingSave()) {
      this.isSubmitting = false;

      return;
    }

    if (party.resource && !(party.resources || []).length) {
      if (!Array.isArray(party.resource)) {
        party.resources = [party.resource];
      } else {
        party.resources = party.resource;
      }
      delete party.resource;
    }

    // The services/resources fields only ever show/offer enabled ids, so a disabled assignment is
    // never visible to touch. If the user left a field exactly as it was when the form opened,
    // restore the party's true original ids (which may include a since-disabled one) rather than
    // saving the enabled-only value the picker displayed — an edit to an unrelated field (e.g.
    // Notes) must not silently drop a disabled service/resource assignment.
    if (this.isEditMode) {
      if (partyIdsEqual(party.services, this.originalCritical?.services)) {
        party.services = this.partyToEdit.services || [];
      }
      if (partyIdsEqual(party.resources, this.originalCritical?.resources)) {
        party.resources = this.partyToEdit.resources || [];
      }
      // hasDisabledServicesOnInit means initEditMode recalculated duration from only the
      // enabled subset of services (possibly 0), overwriting the party's true duration in the
      // form. If the user never touched the duration field since, save the true original value
      // instead of the recalculated one, or a disabled-then-reenabled service permanently loses
      // its duration.
      if (this.hasDisabledServicesOnInit && party.duration === this.originalCritical?.duration) {
        party.duration = this.partyToEdit.duration;
      }
    }

    party.overrideNoResources = this.overrideNoResources;
    party.overrideBlockedTime = this.overrideBlockedTime;

    if (this.shouldForceOverrideNoResources()) {
      party.overrideNoResources = true;
    }

    if (party.type !== PartyTypes.reservation && !Boolean(this.partyToEdit)) {
      party.checkin_time = party.checkin_time || Date.now();
    }

    const successMessage = 'updated successfully';
    const approval = this.getSaveAnywayApproval();
    if (approval) {
      this.confirmSaveAnyway(
        party,
        approval.warning,
        approval.overrides,
        successMessage,
        showSuccessUpdateAlert,
      );

      return;
    }

    this.savePartyAction(party, successMessage, showSuccessUpdateAlert);
  }

  private getSaveAnywayApproval(): SaveAnywayApproval | null {
    if (this.criticalFieldsUnchanged()) {
      return null;
    }

    return buildSaveAnywayApproval({
      resourceSizeAlert: this.resourceSizeAlert,
      overlap: Boolean(this.showOverlappingAlert) && !this.overrideNoResources,
      allowMultipleParties: Boolean(this.allowMultipleParties),
      overlapWarning: tableTakenWarning(Boolean(this.isReservation)),
    });
  }

  private isWorkTimeBlockingSave(): boolean {
    // Refuse the save only while the schedule warning is actually on screen with its "Override?"
    // un-ticked — the same condition that disables Confirm. A guard that is stricter than the
    // button turns a click into a silent no-op; one keyed on isEditMode drops the check for every
    // edit instead of only for the edits that legitimately bypass it.
    return !!this.addEditForm
      && this.showBusinessHoursWarning(this.addEditForm)
      && !this.overrideIsWorkTime;
  }

  private shouldForceOverrideNoResources(): boolean {
    return this.canBypassWorkTime() || Boolean(this.revealForm);
  }

  cancel(): void {
    this.modalController.dismiss();
  }

  ok(showSuccessUpdateAlert = true): void {
    const { phone } = this.party;
    if (phone !== '' && this.parties.find(party => party.phone === phone)) {
      this.modalController.create<unknown, string>(this.confirmModalContentRef.nativeElement)
        .pipe(
          take(1),
          tap((res) => {
            if (res === 'Cancel click') {
              return;
            }
            this.actionAfterCloseModal(showSuccessUpdateAlert);
          }),
          catchError((error) => {
            this.cancel();

            return of(error);
          }),
        )
        .subscribe();

      return;
    }
    this.actionAfterCloseModal(showSuccessUpdateAlert);
  }

  actionAfterCloseModal(showSuccessUpdateAlert = true): void {
    const { size, sizeOther } = this.party;
    if (this.user.waitFields.size &&
      size &&
      typeof size !== 'number' ||
      sizeOther &&
      typeof sizeOther !== 'number') {
      this.showSizeValidationMessage = true;

      return;
    }
    this.isSubmitting = true;
    this.saveParty(showSuccessUpdateAlert);
  }

  isSubmitDisabled(addEditForm: NgForm) {
    if (!addEditForm) {
      return true;
    }

    const isInBusinessHoursAndNoErrors = this.showNoServicesWarning(addEditForm);
    const isAvailableTimeAndNoOverride = Boolean(this.isAvailableTime) && !this.overrideNoResources
      && !this.shortenedDurationState?.hasShortenedDuration;
    const isBlockedTimeAndNoOverride =
      this.isReservation && Boolean(this.blockedTime?.blocked) && !this.overrideBlockedTime;
    const needsShortenedDurationConfirmation =
      Boolean(this.shortenedDurationState?.requiresConfirmation) && !this.shortenedDurationConfirmed;

    if (this.isEditMode) {
      // Every warning this form can raise is mirrored here, except overlap and capacity, which the
      // host approves with "Save anyway" at save time: the schedule warning (until its "Override?"
      // is ticked), the no-services warning, the "no available resource" alert, the blocked-time
      // warning (until its own "Override?" is ticked), and the shortened-duration prompt. What
      // edit mode drops is only the *add*-mode
      // business-hours check below, which an existing party must not re-run.
      return addEditForm.form.invalid || addEditForm.form.pending || this.isSubmitting ||
        (this.showBusinessHoursWarning(addEditForm) && !this.overrideIsWorkTime) ||
        isInBusinessHoursAndNoErrors ||
        isAvailableTimeAndNoOverride ||
        isBlockedTimeAndNoOverride ||
        needsShortenedDurationConfirmation;
    }

    return addEditForm.form.invalid ||
      (this.isOutOfBusinessHours(addEditForm) && !this.shortenedDurationState?.hasShortenedDuration) ||
      addEditForm.form.pending ||
      isInBusinessHoursAndNoErrors ||
      isAvailableTimeAndNoOverride ||
      isBlockedTimeAndNoOverride ||
      needsShortenedDurationConfirmation ||
      this.isSubmitting;
  }

  undoServed(): void {
    this.isSubmitting = true;
    const options = {
      party_id: this.partyToEdit._id,
      seated_time: null,
      canceled_time: null,
    };

    this.markPartyService.setParty(options)
      .pipe(
        tap(() => {
          this.alertsService.addSuccess(
            `moved to ${this.partyToEdit.type === PartyTypes.togo ? 'To Go' : 'waitlist'}`,
            'Success: ',
          );
          this.modalController.dismiss();
          this.isSubmitting = false;
        }),
        catchError(err => {
          this.isSubmitting = false;
          this.alertsService.addError('an error occurred during moving party to Waitlist');

          return of(err);
        }),
      ).subscribe();
  }

  undoCheckIn(): void {
    const party = cloneDeep(this.partyToEdit);
    party.checkin_time = null;
    party.overrideNoResources = this.overrideNoResources;

    if (!this.showOverlappingAlert) {
      party.overrideNoResources = true;
    }

    this.savePartyAction(party, 'check-in was canceled');
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private confirmSaveAnyway(
    party: IParty,
    warning: string,
    overrides: Partial<IParty>,
    successMessage: string,
    showSuccessAlert: boolean,
  ): void {
    this.confirmationModalService.confirm(saveAnywayConfirm(warning))
      .pipe(take(1))
      .subscribe((result) => {
        if (result?.isConfirmed) {
          this.savePartyAction({
            ...party,
            ...overrides,
          }, successMessage, showSuccessAlert);
        } else {
          this.isSubmitting = false;
          this.abortHandOff?.();
        }
      });
  }

  private savePartyAction(
    party: IParty,
    successMessage = 'updated successfully',
    showSuccessAlert = true,
  ): void {
    if (this.isSavingParty) {
      return;
    }

    this.isSubmitting = true;
    this.isSavingParty = true;
    this.partiesService.saveParty(party).pipe(
      tap((data) => {
        this.modalController.dismiss(keepGrantedSeatOverride(data, party));
        if (showSuccessAlert) {
          this.alertsService.add({
            type: IAlertTypes.success,
            heading: 'Success:',
            body: successMessage,
          });
        }
      }),
      catchError((error) => {
        // Seated-table conflict: offer "Seat anyway" (retry with the override flag) instead of a
        // dead-end error, matching the reservation floor-plan modal. The retry runs after this
        // stream finalizes, so isSavingParty is already cleared.
        if (error?.conflict) {
          const action = saveOrSeatAction(Boolean(this.revealForm));

          this.confirmationModalService.confirm(seatedConflictConfirm(error.conflictParties, action))
            .pipe(take(1))
            .subscribe((result) => {
              if (result?.isConfirmed) {
                this.savePartyAction({ ...party, overrideSeatedConflict: true }, successMessage, showSuccessAlert);
              } else {
                this.abortHandOff?.();
              }
            });

          return of(error);
        }

        const { message, reloadData, blockedTime, sizeError } = error;
        // Booked: its approval sends overrideNoResources only; a seated occupant then
        // gets its own seated-table prompt.
        if (reloadData && !blockedTime) {
          this.confirmSaveAnyway(
            party,
            tableTakenWarning(Boolean(this.isReservation)),
            { overrideNoResources: true },
            successMessage,
            showSuccessAlert,
          );

          return of(error);
        }

        if (sizeError) {
          this.confirmSaveAnyway(
            party,
            message || 'The party size does not fit this table.',
            { overridePartySize: true },
            successMessage,
            showSuccessAlert,
          );

          return of(error);
        }

        this.revealForm?.();

        this.alertsService.add({
          time: Date.now(),
          type: IAlertTypes.danger,
          heading: 'Error:',
          body: `new ${this.user.displayOptions?.labels?.customer || 'customer'} not added successfully: ${message}`,
        });

        return of(error);
      }),
      finalize(() => {
        this.isSavingParty = false;
        this.isSubmitting = false;
      }),
    ).subscribe();
  }

  isWhatsAppEnabled() {
    return useWhatsApp(this.user);
  }

  isUsOrCaPhoneNumber(): boolean {
    return isUsOrCaPhoneNumber(this.party.phone);
  }

  changeMessagingProvider() {
    if (!this.party.phone || isUsOrCaPhoneNumber(this.party.phone)) {
      this.party.messagingProvider = null;
    }
  }

  getServiceOptionName = (service: IServiceWaitlist): string => {
    return getServiceOptionNameWithWarning(service);
  };
}
